import {
  createAsyncThunk,
  createAction,
  ActionReducerMapBuilder,
  EntityState,
} from '@reduxjs/toolkit';
import difference from 'lodash/difference';
import { classToPlain } from 'class-transformer';

import { AppError } from '@vk-hr-tek/core/error';
import { Filter } from '@vk-hr-tek/core/filter';
import { showCustomNotification } from '@vk-hr-tek/core/notifications';
import { History } from '@vk-hr-tek/core/history';
import { QueryService } from '@vk-hr-tek/core/query';
import { LocalStorage } from '@vk-hr-tek/core/local-storage';
import { Calendar } from '@vk-hr-tek/core/calendar';

import { CancelReasonItem, EventListItem as Event } from '@app/gen/events';
import { ThunkExtra } from '@app/store';

import {
  EventsState,
  EventsWithRootState,
  startLoading,
  completeLoading,
  setError,
  eventsAdapter,
} from '../events.state';
import { GetEventsDto } from '../../dto';
import { EventsDetailService, EventsListService } from '../../services';
import { SHOW_URGENT_EVENTS_LOCAL_STORAGE_KEY } from '../../constants';

const JANUARY_NUMBER = 0;
const DECEMBER_NUMBER = 11;

export const setFilters = createAction<{
  id: string;
  data: Filter[] | null;
}>('events/setFilters');

const getEventCounts = createAsyncThunk<
  {
    total: number;
    totalMy?: number;
    totalMyTeam?: number;
    totalFromMe?: number;
    finished: number;
  },
  {
    getEventsDto: GetEventsDto;
    availableFilters: Filter[];
  },
  ThunkExtra<EventsWithRootState>
>(
  'events/getEventCounts',
  async (
    { getEventsDto, availableFilters },
    { rejectWithValue, extra: { inject } },
  ) => {
    try {
      const eventsListService = inject(EventsListService);

      const result = await eventsListService.getEventCounts(
        getEventsDto,
        availableFilters,
      );

      return {
        total: result.total_count,
        totalMy: result.total_count_my,
        totalMyTeam: result.total_count_my_group,
        totalFromMe: result.total_count_from_me,
        finished: result.finished_count || 0,
      };
    } catch (err) {
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const getEvents = createAsyncThunk<
  {
    data: Event[];
    parentEventTags: string[];
    tags?: string[];
    companyId?: string;
    isAbsencesPage?: boolean;
    showUrgent?: boolean;
  },
  GetEventsDto & { isAbsencesPage?: boolean },
  ThunkExtra<EventsWithRootState>
>(
  'events/getEvents',
  async (
    getEventsDto,
    { rejectWithValue, getState, dispatch, extra: { inject } },
  ) => {
    try {
      const state = getState().events;
      const eventsService = inject(EventsListService);
      const calendar = inject(Calendar);
      const currentYear = new Date().getFullYear();
      const currentMonth = new Date().getMonth();

      let filters = state.filters;

      const filtersData = {
        tags: getEventsDto.tags,
        companyId: getEventsDto.companyId,
      };

      if (
        !filters.id ||
        !filters.data ||
        filters.id !== getEventsDto.companyId
      ) {
        filters = {
          data: getEventsDto.isAbsencesPage
            ? await eventsService.getAbsenceFilters(filtersData)
            : await eventsService.getFilters(filtersData),
          id: getEventsDto.companyId || '',
        };

        dispatch(setFilters(filters));
      }

      if (currentMonth === JANUARY_NUMBER) {
        await calendar.download(new Date(String(currentYear - 1)));
      }

      if (currentMonth === DECEMBER_NUMBER) {
        await calendar.download(new Date(String(currentYear + 1)));
      }

      await calendar.download(new Date(String(currentYear)));

      dispatch(
        getEventCounts({
          getEventsDto,
          availableFilters: filters.data as Filter[],
        }),
      );

      return await eventsService.get(getEventsDto, filters.data as Filter[]);
    } catch (err) {
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const updateEvents = createAsyncThunk<
  {
    data: Event[];
  },
  GetEventsDto,
  ThunkExtra<EventsWithRootState>
>(
  'events/updateEvents',
  async (
    getEventsDto,
    { rejectWithValue, getState, dispatch, extra: { inject } },
  ) => {
    try {
      const history = inject<History>(History);
      const filters = getState().events.filters.data;
      const showUrgent = getState().events.showUrgentEvents;
      const eventListService = inject(EventsListService);

      const filter = inject(QueryService).parse(history.location.search);

      dispatch(
        getEventCounts({
          getEventsDto: {
            ...getEventsDto,
            showUrgent,
            ...(filter.filters ? { filters: filter.filters } : {}),
          },
          availableFilters: filters || [],
        }),
      );

      const result = await eventListService.get(
        {
          ...getEventsDto,
          showUrgent,
          ...(filter.filters ? { filters: filter.filters } : {}),
        },
        filters || [],
      );

      return result;
    } catch (err) {
      dispatch(
        showCustomNotification([
          {
            type: 'error',
            title: 'Не удалось обновить заявки',
            message: 'Пожалуйста, попробуйте перезагрузить страницу',
          },
        ]),
      );
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const getDocumentCount = createAsyncThunk<
  {
    totalCount: number;
  },
  GetEventsDto,
  ThunkExtra<EventsWithRootState>
>(
  'events/getDocumentCount',
  async (
    getEventsDto,
    { rejectWithValue, getState, dispatch, extra: { inject } },
  ) => {
    try {
      const state = getState().events;
      const eventsService = inject(EventsListService);

      let filters = state.filters;

      if (
        !filters.id ||
        !filters.data ||
        filters.id !== getEventsDto.companyId
      ) {
        filters = {
          data: await eventsService.getFilters(),
          id: getEventsDto.companyId || '',
        };

        dispatch(setFilters(filters));
      }

      return await eventsService.getDocumentCount(
        getEventsDto,
        filters.data || [],
      );
    } catch (err) {
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const getAdditionalEvents = createAsyncThunk<
  {
    data: Event[];
  },
  GetEventsDto & { isAbsencesPage?: boolean },
  ThunkExtra<EventsWithRootState>
>(
  'events/getAdditionalEvents',
  async (
    getEventsDto,
    { rejectWithValue, getState, dispatch, extra: { inject } },
  ) => {
    try {
      const state = getState().events;
      const eventsService = inject(EventsListService);

      let filters = state.filters;
      const showUrgent = state.showUrgentEvents;

      const filtersData = {
        tags: getEventsDto.tags,
        companyId: getEventsDto.companyId,
      };

      if (
        !filters.id ||
        !filters.data ||
        filters.id !== getEventsDto.companyId
      ) {
        filters = {
          data: getEventsDto.isAbsencesPage
            ? await eventsService.getAbsenceFilters(filtersData)
            : await eventsService.getFilters(filtersData),
          id: getEventsDto.companyId || '',
        };

        dispatch(setFilters(filters));
      }

      dispatch(
        getEventCounts({
          getEventsDto: { ...getEventsDto, showUrgent },
          availableFilters: filters.data || [],
        }),
      );

      return await eventsService.get(
        { ...getEventsDto, showUrgent },
        filters.data || [],
      );
    } catch (err) {
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const getCancelReasons = createAsyncThunk<
  CancelReasonItem[],
  { isCandidate?: boolean } | undefined,
  ThunkExtra<EventsWithRootState>
>(
  'events/getCancelReasons',
  async (params, { rejectWithValue, extra: { inject } }) => {
    try {
      const eventService = inject(EventsDetailService);
      const result = await eventService.getCancelReasons({
        isCandidate: params?.isCandidate,
      });

      return result;
    } catch (err) {
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const setShowUrgentEvents = createAsyncThunk<
  void,
  boolean,
  ThunkExtra<EventsWithRootState>
>(
  'events/setShowUrgentEvents',
  async (isShowUrgentEvents, { rejectWithValue, extra: { inject } }) => {
    try {
      const localStorageService = inject<LocalStorage>(LocalStorage);

      localStorageService.setItem(
        SHOW_URGENT_EVENTS_LOCAL_STORAGE_KEY,
        isShowUrgentEvents.toString(),
      );
    } catch (err) {
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const eventsListReducers = (
  builder: ActionReducerMapBuilder<EntityState<Event, string> & EventsState>,
) => {
  builder.addCase(setFilters, (state, action) => {
    state.filters = action.payload;
  });
  builder.addCase(getEvents.pending, (state) => {
    startLoading(state);
  });
  builder.addCase(getEvents.fulfilled, (state, { payload }) => {
    completeLoading(state);

    state.parentEventTags = payload.parentEventTags;
    state.currentIds = payload.data.map(({ id }) => id);
    eventsAdapter.upsertMany(state, payload.data);
  });

  builder.addCase(getAdditionalEvents.fulfilled, (state, { payload }) => {
    completeLoading(state);

    state.currentIds = [
      ...state.currentIds,
      ...payload.data.map(({ id }) => id),
    ];
    eventsAdapter.upsertMany(state, payload.data);
  });
  builder.addCase(getEvents.rejected, (state, { payload, error, meta }) => {
    if (!meta.aborted) {
      setError(state, { payload, error });
    }
  });
  builder.addCase(updateEvents.fulfilled, (state, { payload, meta }) => {
    completeLoading(state);

    const removedIds = difference(
      meta.arg.sortByIds || [],
      payload.data.map(({ id }) => id),
    );

    eventsAdapter.updateMany(
      state,
      payload.data.map((event) => ({
        id: event.id,
        changes: event,
      })),
    );

    eventsAdapter.removeMany(state, removedIds);
  });

  builder.addCase(getDocumentCount.pending, (state) => {
    state.totalDocuments = null;
  });
  builder.addCase(getDocumentCount.fulfilled, (state, { payload }) => {
    state.totalDocuments = payload.totalCount;
  });

  builder.addCase(getEventCounts.fulfilled, (state, { payload }) => {
    state.total = payload.total;
    state.totalMy = payload.totalMy;
    state.totalMyTeam = payload.totalMyTeam;
    state.totalFromMe = payload.totalFromMe;
    state.finished = payload.finished;
  });

  builder.addCase(getCancelReasons.pending, (state) => {
    state.cancelReasons = null;
  });
  builder.addCase(getCancelReasons.fulfilled, (state, { payload }) => {
    state.cancelReasons = payload;
  });

  builder.addCase(setShowUrgentEvents.pending, (state, { meta }) => {
    state.showUrgentEvents = meta.arg;
  });
  builder.addCase(setShowUrgentEvents.rejected, (state, { meta }) => {
    state.showUrgentEvents = !meta.arg;
  });
};
